import { useState, useEffect } from "react";
import type { Property } from "@/hooks/useProperties";

const FAVORITES_STORAGE_KEY = "dem_favorites";
const FAVORITES_UPDATED_EVENT = "dem-favorites-updated";

const readFavoritesFromStorage = (): Property[] => {
  if (typeof window === "undefined") return [];

  const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    // Ensure robust data shape and avoid duplicated cards by cod_ofer.
    const seen = new Set<number>();
    return parsed.filter((item): item is Property => {
      const cod = Number(item?.cod_ofer);
      if (!Number.isFinite(cod) || seen.has(cod)) return false;
      seen.add(cod);
      return true;
    });
  } catch (e) {
    console.error("Failed to parse favorites", e);
    return [];
  }
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<Property[]>([]);

  useEffect(() => {
    setFavorites(readFavoritesFromStorage());

    const syncFavorites = () => {
      setFavorites(readFavoritesFromStorage());
    };

    // Sync between tabs/windows.
    window.addEventListener("storage", syncFavorites);
    // Sync immediately inside the same tab (Header badge, cards, favorites page).
    window.addEventListener(FAVORITES_UPDATED_EVENT, syncFavorites);

    return () => {
      window.removeEventListener("storage", syncFavorites);
      window.removeEventListener(FAVORITES_UPDATED_EVENT, syncFavorites);
    };
  }, []);

  const toggleFavorite = (property: Property) => {
    // Always use the latest persisted value to avoid off-by-one state issues
    // when multiple components use the hook simultaneously.
    const current = readFavoritesFromStorage();
    const isFav = current.some((p) => p.cod_ofer === property.cod_ofer);
    const updated = isFav
      ? current.filter((p) => p.cod_ofer !== property.cod_ofer)
      : [...current, property];

    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(updated));
    setFavorites(updated);
    window.dispatchEvent(new Event(FAVORITES_UPDATED_EVENT));
  };

  const isFavorite = (codOfer: number) => {
    return favorites.some((p) => p.cod_ofer === codOfer);
  };

  return { favorites, toggleFavorite, isFavorite };
};
